import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getStoredLanguage, useTranslation } from "../context/I18nContext";
import { apiBaseUrl } from "../services/auth";
import StudentShell from "../components/StudentShell/StudentShell";
import "./Offers.css";

export default function Offers() {
  const [sections, setSections] = useState({
    "class-types": [],
    languages: [],
    upcoming: [],
    addons: [],
  });
  const { user, effectiveRole } = useAuth();
  const { t } = useTranslation();

  const isConnected = Boolean(user);

  const asArray = (value) => (Array.isArray(value) ? value : []);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const headers = {
          Accept: "application/json",
          "Content-Type": "application/json",
          "Accept-Language": getStoredLanguage(),
        };

        const queryString = `?${new URLSearchParams({
          lang: getStoredLanguage(),
        }).toString()}`;

        const response = await fetch(
          `${apiBaseUrl}/api/auth/admin/packs/${queryString}`,
          {
            method: "GET",
            headers,
          }
        );

        const isJson = response.headers
          .get("content-type")
          ?.includes("application/json");
        const data = isJson ? await response.json() : {};
        if (!response.ok) {
          throw new Error(data?.error || "Failed to load offers packs");
        }

        const packs = asArray(data?.packs);
        const grouped = {
          "class-types": [],
          languages: [],
          upcoming: [],
          addons: [],
        };

        const displayOrderById = new Map();
        for (const pack of packs) {
          displayOrderById.set(
            pack?.slug || pack?.id,
            Number(pack?.offers_display_order || 0)
          );

          const section = pack?.offers_section;
          if (!section || !(section in grouped)) continue;

          grouped[section].push({
            id: pack?.slug || pack?.id,
            packId: pack?.id,
            kind: pack?.offers_kind || "offer-card",
            section,
            accent: pack?.offers_accent || "default",
            categoryKey: pack?.offers_category_key || "",
            titleKey: pack?.offers_title_key || "",
            subtitleKey: pack?.offers_subtitle_key || "",
            descriptionKey: pack?.offers_description_key || "",
            priceKey: pack?.offers_price_key || "",
            billingKey: pack?.offers_billing_key || "",
            featuresKey: pack?.offers_features_key || "",
            ctaKey: pack?.offers_cta_key || "",
            ctaLink: pack?.offers_cta_link || "",
            badgeKey: pack?.offers_badge_key || "",
            noteKey: pack?.offers_note_key || "",
            pointsKey: pack?.offers_points_key || "",
            gradient_start_color: pack?.gradient_start_color || undefined,
            gradient_end_color: pack?.gradient_end_color || undefined,
          });
        }

        for (const sectionId of Object.keys(grouped)) {
          grouped[sectionId].sort(
            (a, b) =>
              (displayOrderById.get(a.id) || 0) -
              (displayOrderById.get(b.id) || 0)
          );
        }

        const next = grouped;
        if (!mounted) return;
        setSections({
          "class-types": asArray(next["class-types"]),
          languages: asArray(next.languages),
          upcoming: asArray(next.upcoming),
          addons: asArray(next.addons),
        });
      } catch (error) {
        console.error("Error fetching offers:", error);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const offerSections = useMemo(
    () => [
      {
        id: "class-types",
        tagKey: "offers.sections.classTypes.tag",
        headingKey: "offers.sections.classTypes.heading",
        descriptionKey: "offers.sections.classTypes.description",
        items: sections["class-types"],
      },
      {
        id: "languages",
        tagKey: "offers.sections.languages.tag",
        headingKey: "offers.sections.languages.heading",
        descriptionKey: "offers.sections.languages.description",
        items: sections.languages,
      },
      {
        id: "upcoming",
        tagKey: "offers.sections.upcoming.tag",
        headingKey: "offers.sections.upcoming.heading",
        descriptionKey: "offers.sections.upcoming.description",
        items: sections.upcoming,
      },
    ],
    [sections]
  );
  const tr = (maybeKey, options) => {
    if (typeof maybeKey !== "string") return "";
    const key = maybeKey.trim();
    if (!key) return "";
    return t(key, options);
  };

  const toList = (maybeKeyOrText) => {
    if (typeof maybeKeyOrText !== "string") return [];
    const raw = maybeKeyOrText.trim();
    if (!raw) return [];

    if (raw.startsWith("offers.")) {
      const translated = tr(raw, { returnObjects: true });
      return asArray(translated).filter(Boolean);
    }

    // Allow literal lists edited in admin (newline- or bullet-separated).
    return raw
      .split(/\n|•/)
      .map((s) => s.trim())
      .filter(Boolean);
  };

  const addons = sections.addons;

  // Every offer opens the contact form with the offer as the subject.
  const contactLink = (item) => {
    const topic = tr(item.titleKey) || tr(item.categoryKey) || item.id;
    const params = new URLSearchParams({ topic: String(topic), from: "offers" });
    return `/contact?${params.toString()}`;
  };

  const page = (
    <div className={`offers-page${isConnected ? " offers-page--app" : ""}`}>
      <section className="offers-hero">
        <div className="offers-shell">
          <span className="offers-hero__tag">{t("offers.heroTag")}</span>
          <h1>{t("offers.heroTitle")}</h1>
          <p>{t("offers.heroDescription")}</p>
        </div>
      </section>

      <section className="offers-section">
        <div className="offers-shell">
          {offerSections.map((section) => (
            <div key={section.id} className="offers-group">
              <div className="offers-section__head offers-section__head--inline">
                <span className="offers-section__tag">{t(section.tagKey)}</span>
                <h2>{t(section.headingKey)}</h2>
                <p>{t(section.descriptionKey)}</p>
              </div>
              <div
                className={`offers-grid ${(() => {
                  if (isConnected) {
                    return "";
                  }
                  if (section.items.length >= 4) return "offers-grid--cols-4";
                  if (section.items.length === 3) return "offers-grid--cols-3";
                  if (section.items.length === 2) return "offers-grid--cols-2";
                  return "offers-grid--cols-1";
                })()}${
                  !isConnected && section.items.length >= 4
                    ? " offers-grid--edge offers-grid--flex"
                    : ""
                }`}
              >
                {section.items.map((item) => (
                  <Link
                    key={item.id}
                    to={contactLink(item)}
                    className={`offer-card offer-card--link offer-card--${
                      item.accent || "default"
                    }`}
                    style={{
                      "--offer-gradient-start":
                        item.gradient_start_color || undefined,
                      "--offer-gradient-end":
                        item.gradient_end_color || undefined,
                    }}
                  >
                    {item.badgeKey && (
                      <span className="offer-card__badge">
                        {tr(item.badgeKey)}
                      </span>
                    )}
                    <div className="offer-card__header">
                      {item.categoryKey ? (
                        <span className="offer-card__category">
                          {tr(item.categoryKey)}
                        </span>
                      ) : null}
                      {item.titleKey ? <h2>{tr(item.titleKey)}</h2> : null}
                      {item.subtitleKey ? (
                        <p className="offer-card__subtitle">
                          {tr(item.subtitleKey)}
                        </p>
                      ) : null}
                    </div>
                    <p className="offer-card__description">
                      {item.descriptionKey ? tr(item.descriptionKey) : ""}
                    </p>
                    <ul className="offer-card__features">
                      {item.featuresKey
                        ? toList(item.featuresKey).map((feature) => (
                            <li key={feature}>{feature}</li>
                          ))
                        : null}
                    </ul>
                    <div className="offer-card__footer">
                      <span className="btn primary offer-card__cta">
                        {item.ctaKey ? tr(item.ctaKey) : t("offers.support.ctaCall")}
                      </span>
                      {item.noteKey && (
                        <p className="offer-card__note">{tr(item.noteKey)}</p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="offers-section offers-addons">
        <div className="offers-shell">
          <div className="offers-section__head">
            <span className="offers-section__tag">
              {t("offers.addons.tag")}
            </span>
            <h2>{t("offers.addons.heading")}</h2>
            <p>{t("offers.addons.description")}</p>
          </div>
          <div className="offers-addons__grid">
            {addons.map((addon) => (
              <Link
                key={addon.id}
                to={contactLink(addon)}
                className="offers-addons__card offers-addons__card--link"
              >
                <h3>{addon.titleKey ? tr(addon.titleKey) : ""}</h3>
                <p>{addon.descriptionKey ? tr(addon.descriptionKey) : ""}</p>
                {addon.pointsKey ? (
                  <ul className="offers-addons__points">
                    {toList(addon.pointsKey).map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                ) : null}
                <div className="offers-addons__footer">
                  <span className="btn ghost">
                    {addon.ctaKey ? tr(addon.ctaKey) : t("offers.support.ctaCall")}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="offers-section offers-support">
        <div className="offers-shell offers-support__content">
          <div className="offers-support__copy">
            <h2>{t("offers.support.heading")}</h2>
            <p>{t("offers.support.description")}</p>
          </div>
          <div className="offers-support__actions">
            <Link to="/contact" className="btn primary">
              {t("offers.support.ctaCall")}
            </Link>
            <Link to="/signup" className="btn ghost">
              {t("offers.support.ctaSignup")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );

  return effectiveRole === "student" ? (
    <StudentShell>{page}</StudentShell>
  ) : (
    page
  );
}
